import { useEffect, useMemo, useState } from "react";
import { BrowserRouter } from "react-router-dom";
import { TouchscreenProvider } from "@touchscreen/hooks";
import { PhoneShell } from "./PhoneShell.js";
import { VenuePersister } from "./VenuePersister.js";
import { EntryScreen } from "./screens/EntryScreen.js";
import { readBootParams, writeVenueRefToUrl } from "./lib/bootParams.js";
import { readLastVenueRef } from "./lib/lastVenue.js";
import { readBootRetryEnv } from "./lib/bootRetryEnv.js";

const BASE_URL = import.meta.env.VITE_API_BASE_URL;
const { retryBaseMs, retryMaxMs } = readBootRetryEnv();

/**
 * Three entry paths converging on a venue reference: Direct (`?venue=` or `/JUKE1234`), Resume
 * (a cached venue from a recent visit), and Find Me (no venue known — resolved interactively). See
 * touchscreen-frontend.md §Phone.
 *
 * The URL is the source of truth for which venue is active: every path above still resolves to a
 * plain `venueRef` in state, but that state is mirrored straight back into `?venue=` so the
 * address bar always shows (and can be used to override) the current venue — handy when devving.
 */
export function App(): React.JSX.Element {
  const { venueRef: directVenueRef, legacyVenuePath, devKey, debugSkipGeofence } = useMemo(
    () => readBootParams(window.location.search, window.location.pathname),
    [],
  );
  const [venueRef, setVenueRef] = useState<string | null>(() => directVenueRef ?? readLastVenueRef());
  // Whether the *next* provider mount should start already geofence-passed — set true when the
  // venue was just location-verified via Find Me's confirm step, or forced by the debug flag.
  // Reset alongside `venueRef` on every switch, so it never leaks from one venue to the next.
  const [initialGeofencePassed, setInitialGeofencePassed] = useState(debugSkipGeofence);

  useEffect(() => {
    writeVenueRefToUrl(venueRef);
  }, [venueRef]);

  function handleVenueChosen(ref: string, opts?: { viaNearby?: boolean }): void {
    setInitialGeofencePassed(debugSkipGeofence || Boolean(opts?.viaNearby));
    setVenueRef(ref);
  }

  // Drops back to Find Me — used for an invalid resumed venue, and for every deliberate
  // "change venue" entry point (Now Playing bar, My Music, and the request flow's out-of-range
  // case). There's no partial state to preserve: hitting this always means starting again.
  function resetVenue(): void {
    setInitialGeofencePassed(debugSkipGeofence);
    setVenueRef(null);
  }

  if (!venueRef) {
    return <EntryScreen baseUrl={BASE_URL} onVenueChosen={handleVenueChosen} />;
  }

  return (
    <TouchscreenProvider
      baseUrl={BASE_URL}
      surface="phone"
      venueRef={venueRef}
      devKey={devKey ?? undefined}
      retryBaseMs={retryBaseMs}
      retryMaxMs={retryMaxMs}
      initialGeofencePassed={initialGeofencePassed}
      onResetVenue={resetVenue}
    >
      <VenuePersister venueRef={venueRef} />
      <BrowserRouter>
        <PhoneShell onVenueInvalid={resetVenue} legacyVenuePath={legacyVenuePath} />
      </BrowserRouter>
    </TouchscreenProvider>
  );
}
